// LUCKY DRAW — deterministic, weighted sampling shared by the app and the
// simulation gate. The engine still receives an ordinary resolved 5-card deck;
// this module only decides which three owned fighters complete it.

import { starCost } from './deckrules.js';

export const DECK_PICK = 2;
export const DRAW_COUNT = 3;
export const COLD_WEIGHT = 1.6;
export const HOT_WEIGHT = 0.6;

export function seed32(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value >>> 0;
  const s = String(value ?? '0');
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed) {
  let a = seed32(seed);
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function heatFor(id, history = {}) {
  const recent = new Set((history.recent || []).flat());
  if (recent.has(id)) return 'hot';
  const fielded = history.fielded || {};
  if (!fielded[id]) return 'cold';
  return 'neutral';
}

export function heatWeight(id, history) {
  const heat = heatFor(id, history);
  return heat === 'cold' ? COLD_WEIGHT : heat === 'hot' ? HOT_WEIGHT : 1;
}

// `budget` (stars left for the drawn cards, or null): each reel only lands on
// a card that still leaves room for the cheapest possible remaining reels. If
// nothing fits, the reel takes the cheapest card (a legal filler where one
// exists; solo may run over, as it always could).
function takeWeighted(pool, weightById, rng, count, budget = null) {
  const left = pool.slice();
  const out = [];
  let room = budget;
  while (out.length < count && left.length) {
    let cand = left;
    if (room != null) {
      const need = count - out.length - 1;
      const cheap = left.map(starCost).sort((a, b) => a - b);
      cand = left.filter(card => {
        const rest = cheap.slice();
        rest.splice(rest.indexOf(starCost(card)), 1);
        return starCost(card) + rest.slice(0, need).reduce((n, c) => n + c, 0) <= room;
      });
      if (!cand.length) {
        cand = [left.slice().sort((a, b) => starCost(a) - starCost(b) || a.id.localeCompare(b.id))[0]];
      }
    }
    const total = cand.reduce((n, card) => n + (weightById[card.id] || 0), 0);
    let roll = rng() * total;
    let pick = cand[cand.length - 1];
    for (let i = 0; i < cand.length; i++) {
      roll -= weightById[cand[i].id] || 0;
      if (roll <= 0) { pick = cand[i]; break; }
    }
    left.splice(left.indexOf(pick), 1);
    out.push(pick);
    if (room != null) room -= starCost(pick);
  }
  return out;
}

// debugSeed accepts `type`, `type:iron`, etc. This is deliberately query-only
// QA behavior: production seeds always take the weighted path.
// pickCount/drawCount default to the V1 2+3 draw; THE FORGE runs 3 picks + 2
// reels under the star budget (`budget`: total stars for the whole squad).
export function resolveDraw({ cards, owned = () => true, picks = [], history = {}, seed,
  debugSeed = null, pickCount = DECK_PICK, drawCount = DRAW_COUNT, budget = null } = {}) {
  const DRAW_COUNT = drawCount;
  const picked = new Set(picks);
  const pool = (cards || []).filter(card => card.role !== 'support' && owned(card.id) && !picked.has(card.id));
  if (pool.length < DRAW_COUNT) throw new Error(`LUCKY DRAW needs ${DRAW_COUNT} unpicked owned fighters`);
  const weights = Object.fromEntries(pool.map(card => [card.id, heatWeight(card.id, history)]));
  const actualSeed = seed32(seed);
  const forced = typeof debugSeed === 'string' && debugSeed.toLowerCase().startsWith('type')
    ? debugSeed.toLowerCase().split(':')[1] || null : null;
  let drawPool = pool;
  if (typeof debugSeed === 'string' && debugSeed.toLowerCase().startsWith('type')) {
    // debug-only: if the OWNED pool has no type with three fighters (small QA guest
    // collections), widen to the catalogue so the forced jackpot is always reachable
    const wide = (cards || []).filter(card => card.role !== 'support' && !picked.has(card.id));
    const sameType = (list, t) => list.filter(card => String(card.type).toLowerCase() === String(t).toLowerCase());
    const types = forced ? [forced] : [...new Set(wide.map(card => card.type))].sort();
    const type = types.find(t => sameType(pool, t).length >= DRAW_COUNT) || types.find(t => sameType(wide, t).length >= DRAW_COUNT);
    if (type) drawPool = sameType(pool, type).length >= DRAW_COUNT ? sameType(pool, type) : sameType(wide, type);
    for (const card of drawPool) if (!(card.id in weights)) weights[card.id] = heatWeight(card.id, history);
  }
  const kept = picks.slice(0, pickCount);
  const byId = Object.fromEntries((cards || []).map(card => [card.id, card]));
  const room = budget == null ? null : budget - kept.reduce((n, id) => n + starCost(byId[id]), 0);
  const drawnCards = takeWeighted(drawPool, weights, mulberry32(actualSeed), DRAW_COUNT, room);
  const drawn = drawnCards.map(card => card.id);
  const result = [...kept, ...drawn];
  return {
    pool: pool.map(card => card.id), weights, seed: actualSeed, result, drawn,
    heat: Object.fromEntries(pool.map(card => [card.id, heatFor(card.id, history)])),
  };
}

export function deriveMultiplayerDrawSeed(roomSeed, side) {
  return seed32(`nomadmon-lucky-draw:${roomSeed}:${side || 'A'}`);
}

export function jackpotFor(draw, cardsById) {
  const cards = (draw.drawn || []).map(id => cardsById[id]).filter(Boolean);
  if (cards.length < 2 || cards.length !== (draw.drawn || []).length) return null;
  if (cards.every(card => card.type === cards[0].type)) {
    return { kind: 'type', text: 'TYPE JACKPOT', type: cards[0].type };
  }
  if (cards.some(card => card.rarity === 'mythic' || card.rarity === 'eternal' || card.altOf)) {
    return { kind: 'god', text: 'GOD DRAW' };
  }
  if (cards.every(card => draw.heat?.[card.id] === 'cold')) {
    return { kind: 'fresh', text: 'FRESH BLOOD' };
  }
  return null;
}
